<template>
    <Head>
        <title>{{ pageData.props.page?.title || '학생 로그인' }}</title>
    </Head>
    <div class="student_login_wrap">
        <div class="container">
            <a href="/" class="login_logo">
                <img src="@/assets/img/logo_gray.svg" />
            </a>
            <div class="login">
                <ol class="login_step">
                    <li :class="{ current: loginStep === 1 }"></li>
                    <li :class="{ current: loginStep === 2 }"></li>
                    <li :class="{ current: loginStep > 2 }"></li>
                </ol>

                <StepClassroom v-if="loginStep === 1" @next="updateClassroom" />
                <StepStudent v-if="loginStep === 2" :key="classroom?.id" :classroom="classroom" @next="updateStudent" />
                <StepPin
                    v-if="loginStep === 3"
                    :classroom="classroom"
                    :student="student"
                    @login="handleLoginAction"
                    @next="handleDoneAction"
                />
                <div v-if="loginStep === 4" class="login_title">
                    <h3>{{ classroom?.teacher?.school }} {{ classroom?.txt_grade }} {{ classroom?.class_name }}</h3>
                    <span>{{ student?.classroom_number }}번 {{ student?.name }}</span>
                    <p>{{ $t('pages.student.auth.login.pinSet') }}</p>
                </div>
            </div>
            <div class="btns">
                <button v-if="loginStep === 4" class="btn btn_danger btn_round" @click="handleLoginAction()">{{
                    $t('pages.student.auth.login.buttons.enter')
                }}</button>
                <button v-else-if="student" class="btn btn_default" @click="resetLogin('student')">
                    {{ $t('pages.student.auth.login.buttons.otherStudent') }}
                </button>
                <button v-else-if="classroom" class="btn btn_default" @click="resetLogin('classroom')">
                    {{ $t('pages.student.auth.login.buttons.otherClassroom') }}
                </button>

                <!-- PWA 설치 버튼 (학급 선택 이후 표시) -->
                <button v-if="shouldShowPWAButton" class="btn btn_default" @click="showPWAConfirm">
                    <i class="fa-solid fa-download"></i>
                    {{ $t('pages.student.auth.login.buttons.pwa') }}
                </button>
            </div>
        </div>
        <button class="language_btn" :class="{ [locale]: true }" @pointerdown="isLanguageModalShow = true">
            <img src="@/assets/img/ico/lang/ico_earth.png" alt="language" />
        </button>
    </div>

    <LanguageSettingModal v-model:is-show="isLanguageModalShow" />

    <!-- PWA 설치 확인 모달 -->
    <div v-if="showPWAConfirmDialog" class="pwa-confirm-modal">
        <div class="pwa-confirm-content">
            <h3>{{ $t('pages.student.auth.login.pwa.title') }}</h3>
            <p>{{ $t('pages.student.auth.login.pwa.description') }}</p>
            <div class="pwa-confirm-buttons">
                <button class="btn btn-secondary" @click="cancelPWAInstall">{{
                    $t('pages.student.auth.login.pwa.buttons.next')
                }}</button>
                <button class="btn btn-primary" @click="confirmPWAInstall">{{
                    $t('pages.student.auth.login.pwa.buttons.create')
                }}</button>
            </div>
        </div>
    </div>

    <!-- 알림 모달 -->
    <DefaultModal v-model:isShow="isShowNotification">
        <template #message>{{ notificationMessage }}</template>
        <template #btns>
            <button class="btn btn_primary" @click="closeNotification">확인</button>
        </template>
    </DefaultModal>
</template>

<script setup lang="ts">
    import { ref, onMounted, watch, computed } from 'vue';
    import { get } from 'lodash';
    import { useForm, usePage, router, Head } from '@inertiajs/vue3';
    import StepClassroom from './login/StepClassroom.vue';
    import StepStudent from './login/StepStudent.vue';
    import StepPin from './login/StepPin.vue';

    import { Classroom } from '@/app/api/models/classroom';
    import { Student } from '@/app/api/models/student';
    import { PageProps } from '@/app/types';
    import { getClassroomByHash } from '@/app/api/login';
    import DefaultModal from '@/app/components/modals/DefaultModal.vue';
    import LanguageSettingModal from '@/app/components/modals/LanguageSettingModal.vue';
    import { useSystemStoreWithOut } from '@/app/stores/system';

    const page = usePage<
        PageProps<{
            classroom: Classroom | null; // props는 null일 수 있음을 명시
            student: Student | null;
        }>
    >();

    const pageData = usePage<
        PageProps<{
            classroom: Classroom;
            student: Student;
        }>
    >();

    const classroom = ref<Classroom | null>(pageData.props.classroom);
    const student = ref<Student | null>(pageData.props.student);
    const loginStep = ref(1);
    const isLoading = ref(true);

    // PWA 설치 관련 상태
    const deferredPrompt = ref<any>(null);
    const showPWAConfirmDialog = ref(false);
    const isPWAInstalled = ref(false);
    const codeHash = ref<string | null>(null);

    const systemStore = useSystemStoreWithOut();
    const locale = computed(() => systemStore.locale);
    const isLanguageModalShow = ref(false);
    const isShowNotification = ref(systemStore.getModalState.show);
    const notificationMessage = ref(systemStore.getModalState.message);

    const showNotification = (message: string) => {
        isShowNotification.value = true;
        notificationMessage.value = message;
    };

    const closeNotification = () => {
        isShowNotification.value = false;
        notificationMessage.value = '';
    };

    // 알림 모달 상태 변경 시 시스템 스토어 상태 업데이트
    watch(isShowNotification, (newVal) => {
        if (!newVal) {
            systemStore.hideModalState();
        }
    });

    const formData = useForm({
        id: 0,
    });

    // PWA 버튼 표시 조건을 computed로 관리
    const shouldShowPWAButton = computed(() => {
        // deferredPrompt(설치 이벤트)가 있고, 아직 설치 안됐고, 2단계 이상일 때만 표시
        return !!deferredPrompt.value && !isPWAInstalled.value && loginStep.value >= 2 && !!codeHash.value;
    });

    // PWA 설치 상태 확인
    const checkPWAInstalled = () => {
        const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
        isPWAInstalled.value = isStandalone;
    };

    const handleLoginAction = (id?: number) => {
        if (id) {
            formData.id = id;
        }
        formData.post(route('app.login.check.pin'));
    };

    // PWA 설치 확인 모달 표시
    const showPWAConfirm = () => {
        showPWAConfirmDialog.value = true;
    };
    const cancelPWAInstall = () => {
        showPWAConfirmDialog.value = false;
    };
    const confirmPWAInstall = () => installPWA();

    // PWA 설치 함수
    const installPWA = async () => {
        showPWAConfirmDialog.value = false;
        const currentPrompt = deferredPrompt.value;

        if (currentPrompt) {
            try {
                currentPrompt.prompt();
                const { outcome } = await currentPrompt.userChoice;
                if (outcome === 'accepted') {
                    isPWAInstalled.value = true;
                }
            } finally {
                deferredPrompt.value = null; // 사용 후에는 반드시 초기화
                window.__deferredPrompt = null;
            }
        }
    };

    // PWA 이벤트 리스너를 onMounted 밖, setup 스코프에 직접 선언합니다.
    // 이렇게 하면 컴포넌트 생명주기와 무관하게 이벤트가 발생하자마자 ref가 업데이트됩니다.
    // ====================================================================
    if (typeof window !== 'undefined') {
        window.addEventListener('beforeinstallprompt', (e) => {
            console.log('✅✅✅ beforeinstallprompt 이벤트가 성공적으로 발생했습니다! ✅✅✅');
            e.preventDefault();
            deferredPrompt.value = e;
        });

        window.addEventListener('appinstalled', () => {
            console.log('✅ PWA 설치 완료!');
            deferredPrompt.value = null;
            isPWAInstalled.value = true;
        });
    }

    // PWA manifest를 동적으로 업데이트하는 함수
    const updatePWAManifest = (classroomCodeHash: string) => {
        // 기존 manifest link 태그 제거
        const existingManifest = document.querySelector('link[rel="manifest"]');
        if (existingManifest) {
            existingManifest.remove();
        }

        // 새로운 manifest link 태그 추가 (쿼리 파라미터 포함)
        const manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        manifestLink.href = `/manifest.json?classroom_code_hash=${encodeURIComponent(classroomCodeHash)}`;
        manifestLink.crossOrigin = 'anonymous'; // CORS 문제 해결
        document.head.appendChild(manifestLink);

        // 브라우저가 새로운 manifest를 인식하도록 약간의 지연 후 이벤트 발생
        setTimeout(() => {
            window.dispatchEvent(new Event('beforeinstallprompt-reset'));
        }, 100);
    };

    const updateClassroom = (data: Classroom, incrementStep = true) => {
        if (incrementStep) {
            loginStep.value++;
        }
        if (data && data.classroom_code_hash) {
            codeHash.value = data.classroom_code_hash;
        } else if (!codeHash.value) {
            const urlParams = new URLSearchParams(window.location.search);
            codeHash.value = urlParams.get('classroom_code_hash');
        }
        classroom.value = data;
        if (classroom.value && codeHash.value && !classroom.value.classroom_code_hash) {
            classroom.value.classroom_code_hash = codeHash.value;
        }

        // 학급코드가 설정되었을 때 PWA manifest 동적 업데이트
        if (codeHash.value) {
            updatePWAManifest(codeHash.value);
        }
    };

    const updateStudent = (data: Student) => {
        loginStep.value++;
        student.value = data;
        formData.id = data.id;
    };

    const resetLogin = (type: string) => {
        const params: { type: string; classroom_code_hash?: string } = { type };

        if (codeHash.value) {
            params.classroom_code_hash = codeHash.value;
        }

        router.post(
            route('app.login.reset', params),
            {},
            {
                onSuccess: (response) => {
                    console.log(response);
                    // 상태 초기화
                    if (type === 'classroom') {
                        classroom.value = null;
                        student.value = null;
                        loginStep.value = 1;
                    } else if (type === 'student') {
                        student.value = null;
                        loginStep.value = 2;
                    }
                },
            },
        );
    };

    const handleDoneAction = (id: number) => {
        loginStep.value++;
        formData.id = id;
    };

    // PWA 이벤트 리스너 설정
    const setupPWAEventListeners = () => {
        window.addEventListener('beforeinstallprompt', (e) => {
            console.log('beforeinstallprompt 이벤트 발생');
            e.preventDefault();

            // ref와 window 객체 양쪽에 모두 저장
            deferredPrompt.value = e;
            (window as any).__deferredPrompt = e; // 전역 변수로 저장하여 페이지 이동 시에도 유지
        });

        window.addEventListener('appinstalled', () => {
            console.log('PWA 설치 완료');
            deferredPrompt.value = null;
            (window as any).__deferredPrompt = null;
            isPWAInstalled.value = true;
        });
    };

    // 페이지의 props가 변경될 때마다 이 로직이 실행됩니다.
    watch(
        () => page.props,
        (newProps) => {
            if (newProps.flash.message) {
                showNotification(get(newProps.flash, 'message[1]', '') as string);
            }
            const newClassroom = newProps.classroom;
            const newStudent = newProps.student;

            if (newClassroom && !newStudent) {
                loginStep.value = 2;
                updateClassroom(newClassroom, false);
            } else if (newClassroom && newStudent) {
                loginStep.value = 3;
            } else {
                loginStep.value = 1;
            }
        },
        { deep: true, immediate: true },
    );

    onMounted(async () => {
        console.log('[Login.vue] onMounted: 컴포넌트 마운트됨');
        // 페이지가 로드될 때 PWA 설치 상태만 확인합니다.
        checkPWAInstalled();

        if (window.__deferredPrompt) {
            console.log('[Login.vue] 전역에 저장된 PWA 프롬프트를 가져왔습니다.');
            deferredPrompt.value = window.__deferredPrompt;
        }

        const urlParams = new URLSearchParams(window.location.search);
        codeHash.value = urlParams.get('classroom_code_hash');

        // PWA 이벤트 리스너 설정 (가장 먼저 실행)
        setupPWAEventListeners();

        if (codeHash.value) {
            if (!classroom.value) {
                try {
                    const { data } = await getClassroomByHash({ classroom_code_hash: codeHash.value });

                    if (data.success && data.data) {
                        classroom.value = data.data;
                        if (classroom.value) {
                            classroom.value.classroom_code_hash = codeHash.value;
                        }

                        if (student.value) {
                            loginStep.value = 3;
                        } else {
                            loginStep.value = 2;
                        }
                    }
                } catch (error) {
                    console.error('Error fetching classroom data:', error);
                }
            } else {
                if (classroom.value) {
                    classroom.value.classroom_code_hash = codeHash.value;

                    if (student.value) {
                        loginStep.value = 3;
                    } else {
                        loginStep.value = 2;
                    }
                }
            }
        }

        isLoading.value = false;
    });

    // Watch specifically for loginStep changes to handle PWA button visibility
    watch(loginStep, (newStep) => {
        console.log('Login step changed to:', newStep);

        // 단계가 2 이상으로 변경될 때 PWA 설치 가능 상태를 다시 활성화
        if (newStep >= 2 && codeHash.value && !isPWAInstalled.value) {
            console.log('Re-enabling PWA install option on step change to', newStep);
        }
    });
</script>

<style scoped>
    /* PWA 확인 모달 스타일 */
    .pwa-confirm-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .pwa-confirm-content {
        background-color: white;
        padding: 20px;
        border-radius: 10px;
        width: 80%;
        max-width: 400px;
        text-align: center;
    }

    .pwa-confirm-content h3 {
        margin-bottom: 10px;
        font-size: 18px;
    }

    .pwa-confirm-content p {
        margin-bottom: 20px;
        color: #666;
    }

    .pwa-confirm-buttons {
        display: flex;
        justify-content: space-between;
    }

    .pwa-confirm-buttons button {
        flex: 1;
        margin: 0 5px;
        padding: 10px;
        border-radius: 5px;
        border: none;
        cursor: pointer;
    }

    .btn-secondary {
        background-color: #f1f1f1;
        color: #333;
    }

    .btn-primary {
        background-color: #3498db;
        color: white;
    }
</style>
